import { useState } from 'react';
import { useLocation } from 'react-router';

import { isFromWorkspace, isWorkspaceRoute } from '../../utils/workspaceNavState';

/**
 * Whether the user is viewing the workspace right now:
 * on the canvas (`/collections`, `/collections/:id`), or on an artifact page
 * they opened from the canvas or the sidebar (the history entry carries
 * {@link FROM_WORKSPACE_STATE}). The shell shows the workspace sidebar only
 * here, and the rail lights Workspace only here.
 *
 * Derived from the route and the history entry, so Back and Forward land on
 * whatever each entry was. One stickiness: a navigation that stays on the SAME
 * page (a search-param change, such as a protocol's version picker, which
 * pushes an entry without state) keeps the context the page already had, so
 * the sidebar does not vanish under the user mid-view. Adjusted during render
 * (React's prop-driven reset pattern), not in an effect, so no frame shows the
 * wrong answer.
 */
export function useInWorkspaceContext(): boolean {
  const { pathname, state } = useLocation();
  const direct = isWorkspaceRoute(pathname) || isFromWorkspace(state);
  const [last, setLast] = useState({ pathname, inContext: direct });
  const inContext = direct || (last.pathname === pathname && last.inContext);
  if (last.pathname !== pathname || last.inContext !== inContext) {
    setLast({ pathname, inContext });
  }
  return inContext;
}
